import React, { memo, useState, useEffect } from 'react';
import { Handle, Position, useReactFlow } from 'reactflow';
import CustomResizer from './CustomResizer';
import { Card, Image, Progress, Typography, Button, App, Select, InputNumber, Upload } from 'antd'; // App imported
import { FileImageOutlined, PictureOutlined, FontSizeOutlined, PlayCircleFilled, LoadingOutlined, ExpandAltOutlined, InboxOutlined, DeleteOutlined } from '@ant-design/icons';

import { useParams } from 'react-router-dom';
import api from '../../../api/client';
import NodeLabel from './NodeLabel';
import { TYPE_COLORS, getHandleStyle, HANDLE_ICON_STYLE } from './nodeStyles';
import { useNodeTitle } from './useNodeTitle';
import { useGeneration } from '../../../context/GenerationContext';

const { Meta } = Card;
const { Text } = Typography;
const { Dragger } = Upload;

const MediaNode = ({ id, data, selected }) => {
    const { message } = App.useApp(); // Use themed message
    const { projectId: canvasId } = useParams();
    const { getNodes, getEdges, setNodes } = useReactFlow();
    const [generating, setGenerating] = useState(false);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewTarget, setPreviewTarget] = useState(null);
    const [hovered, setHovered] = useState(false);
    const { trackGeneration } = useGeneration();

    // Handles are visible when node is selected or hovered
    const handlesVisible = selected || hovered;

    useEffect(() => {
        if (previewTarget && data.src === previewTarget) {
            setPreviewVisible(true);
            setPreviewTarget(null);
        }
    }, [data.src, previewTarget]);
    const isUploadNode = data.isUpload || data.iconType === 'cloud-sync' || data.label === 'Upload';
    const onTitleChange = useNodeTitle(id, data);

    // Parameters with defaults
    const numImages = data.numImages || 1;
    const aspectRatio = data.aspectRatio || "1:1";
    const outputFormat = data.outputFormat || "png";
    const resolution = data.resolution || "1K";
    const mode = data.mode || 'nanobananapro'; // 'nanobananapro' or 'nanobananaproedit'

    const updateData = (key, value) => {
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        [key]: value
                    }
                };
            }
            return node;
        }));

        // Persist to DB
        if (data.onSave) {
            data.onSave(id, { [key]: value });
        }
    };

    const handleGenerate = async () => {
        setGenerating(true);
        try {
            // Find connected source node (looking for edge connected to 'left-top')
            const appEdges = getEdges();
            const sourceEdge = appEdges.find(
                (e) => e.target === id && e.targetHandle === 'left-top'
            );

            if (!sourceEdge) {
                message.warning('Please connect a text node to the top-left handle first.');
                setGenerating(false);
                return;
            }

            const appNodes = getNodes();
            const sourceNode = appNodes.find((n) => n.id === sourceEdge.source);

            if (!sourceNode || !sourceNode.data || !sourceNode.data.text) {
                console.log(sourceNode);
                message.warning('Connected node has no text content.');
                setGenerating(false);
                return;
            }

            const prompt = sourceNode.data.text;

            // Find ALL connected image source edges (looking for edge connected to 'left-bottom')
            const imageSourceEdges = appEdges.filter(
                (e) => e.target === id && e.targetHandle === 'left-bottom'
            );

            const connectedImageUrls = [];
            imageSourceEdges.forEach(edge => {
                const imgNode = appNodes.find((n) => n.id === edge.source);
                if (imgNode && imgNode.data && imgNode.data.src) {
                    connectedImageUrls.push(imgNode.data.src);
                }
            });

            let response;
            if (mode === 'nanobananaproedit') {
                if (connectedImageUrls.length === 0) {
                    message.error('Edit mode requires at least one image input.');
                    setGenerating(false);
                    return;
                }
                // Image-to-Image / Edit Mode
                response = await api.post('/fal/nanobanana/edit', {
                    prompt,
                    image_urls: connectedImageUrls,
                    num_images: numImages,
                    aspect_ratio: aspectRatio,
                    output_format: outputFormat,
                    resolution
                });
            } else if (mode === 'gemini-3-pro-image-preview/edit') {
                if (connectedImageUrls.length === 0) {
                    message.error('Gemini Edit mode requires at least one image input.');
                    setGenerating(false);
                    return;
                }
                // Gemini 3 Pro Edit Mode
                response = await api.post('/fal/gemini/edit', {
                    prompt,
                    image_urls: connectedImageUrls,
                    num_images: numImages,
                    aspect_ratio: aspectRatio,
                    output_format: outputFormat,
                    resolution
                });
            } else {
                // Text-to-Image Mode (nanobananapro)
                console.log('Sending generation request...', { prompt, numImages, aspectRatio, outputFormat, resolution });
                response = await api.post('/fal/nanobanana', {
                    prompt,
                    num_images: numImages,
                    aspect_ratio: aspectRatio,
                    output_format: outputFormat,
                    resolution
                });
            }

            const { request_id, model_id } = response.data;
            // Use returned model_id or fallback to local logic (safety net)
            const finalModelId = model_id || (mode === 'nanobananaproedit' ? 'fal-ai/nano-banana-pro/edit' :
                mode === 'gemini-3-pro-image-preview/edit' ? 'fal-ai/gemini-3-pro-image-preview/edit' :
                    'fal-ai/nano-banana-pro');

            // setGenerating(false); // REMOVED: Keep glowing until complete/fail

            trackGeneration(request_id, finalModelId, {
                prompt,
                type: 'Image',
                canvas_id: canvasId,
                node_id: id,
                promptProp: prompt
            }, {
                onComplete: async (result) => {
                    console.log('Generation response received:', result);
                    try {
                        // Backend normalizes Gemini result structure to result.images
                        if (result && result.images && result.images.length > 0) {
                            message.success('Image Generated!');

                            const processedImages = result.images;
                            const indexUrl = processedImages[0].url;

                            setNodes((nds) => nds.map((node) => {
                                if (node.id === id) {
                                    return {
                                        ...node,
                                        data: {
                                            ...node.data,
                                            src: indexUrl,
                                            generatedImages: processedImages
                                        }
                                    };
                                }
                                return node;
                            }));

                            if (data.onSave) {
                                data.onSave(id, { src: indexUrl, generatedImages: processedImages });
                            }

                            message.success('Image Saved!');
                        } else {
                            console.error('Unexpected response format or failure:', result);
                            message.error('Failed to generate image: Invalid response');
                        }
                    } catch (err) {
                        console.error("Error processing result", err);
                    } finally {
                        setGenerating(false);
                    }
                },
                onFail: (error) => {
                    console.error("Generation failed via queue:", error);
                    setGenerating(false);
                    message.error("Generation failed");
                }
            });

            return;
        } catch (error) {
            console.error('Generation error:', error);
            message.error('Generation failed: ' + (error.response?.data?.error || error.message));
            console.error('Full generation error object:', error);
        }
    };

    const handleUpload = async (options) => {
        const { file, onSuccess, onError } = options;

        // Optimistic update
        updateData('uploading', true);
        updateData('progress', 0);

        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('bucket', 'media-uploads');

            // Pass onUploadProgress if api client supports it (axios does)
            const response = await api.post('/storage/upload', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    updateData('progress', percentCompleted);
                }
            });

            if (response.data && response.data.url) {
                // Determine file type from extension or mime
                const isVideo = file.type.startsWith('video/') || response.data.url.match(/\.(mp4|webm|mov)$/i);

                // Update node data
                setNodes((nds) => nds.map((node) => {
                    if (node.id === id) {
                        return {
                            ...node,
                            data: {
                                ...node.data,
                                src: response.data.url,
                                label: file.name,
                                uploading: false,
                                isVideo: isVideo,
                                // If we want to store mime type
                                mimeType: file.type
                            }
                        };
                    }
                    return node;
                }));

                // Persist
                if (data.onSave) {
                    data.onSave(id, {
                        src: response.data.url,
                        label: file.name,
                        isVideo: isVideo
                    });
                }

                message.success('File uploaded successfully');
                onSuccess("Ok");
            }
        } catch (error) {
            console.error('Upload failed:', error);
            message.error('Upload failed.');
            updateData('uploading', false);
            onError({ error });
        }
    };

    const clearImage = (e) => {
        e.stopPropagation();
        setNodes((nds) => nds.map((node) => {
            if (node.id === id) {
                return {
                    ...node,
                    data: {
                        ...node.data,
                        src: null,
                        generatedImages: [],
                        isVideo: false
                    }
                };
            }
            return node;
        }));
        if (data.onSave) {
            data.onSave(id, { src: null, generatedImages: [], isVideo: false });
        }
    };
    return (
        <div
            style={{ position: 'relative', height: '100%', width: '100%', padding: 20 }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onDoubleClick={(e) => e.stopPropagation()}
        >
            <CustomResizer
                isVisible={selected}
                minWidth={100}
                minHeight={100}
                onResizeEnd={(event, params) => {
                    const { width, height, x, y } = params;
                    if (data.onResize) {
                        data.onResize(id, { width, height, x, y });
                    }
                }}
            />
            <NodeLabel
                title={data.nodeTitle || 'Media'}
                iconType={data.iconType || 'picture'}
                color={data.nodeColor || '#1890ff'}
                onTitleChange={onTitleChange}
            >
                {!isUploadNode && (
                    <div className="nodrag" onMouseDown={(e) => e.stopPropagation()} style={{ marginLeft: 0 }}>
                        <Select
                            value={mode}
                            onChange={(v) => updateData('mode', v)}
                            options={[
                                { value: 'nanobananapro', label: 'Nano Banana Pro' },
                                { value: 'nanobananaproedit', label: 'Nano Banana Pro Edit' },
                                { value: 'gemini-3-pro-image-preview/edit', label: 'Gemini 3 Pro' }
                            ]}
                            className="pill-select"
                            dropdownMatchSelectWidth={false}
                            getPopupContainer={triggerNode => triggerNode.parentNode}
                            style={{ width: 140 }}
                        />
                    </div>
                )}
            </NodeLabel>

            {/* Upload node: single right handle only */}
            {isUploadNode && (
                <Handle type="source" position={Position.Right} style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)} />
            )}

            {/* Image Generator: two left input handles */}
            {!isUploadNode && (
                <>
                    <Handle type="target" position={Position.Left} id="left-top" style={getHandleStyle(TYPE_COLORS.text, 'left-top', handlesVisible)} />
                    <Handle type="target" position={Position.Left} id="left-bottom" style={getHandleStyle(TYPE_COLORS.image, 'left-bottom', handlesVisible)} />
                    <Handle type="source" position={Position.Right} style={getHandleStyle(TYPE_COLORS.image, 'right', handlesVisible)} />

                    {/* Play Button */}
                    <Button
                        type="primary"
                        shape="circle"
                        icon={generating ? <LoadingOutlined style={{ fontSize: 72 }} /> : <PlayCircleFilled style={{ fontSize: 72 }} />}
                        onClick={handleGenerate}
                        style={{
                            position: 'absolute',
                            right: -180,
                            bottom: 0,
                            width: 150,
                            height: 150,
                            zIndex: 100,
                            boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
                            border: '12px solid #161b22',
                            background: '#722ed1',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    />
                </>
            )}

            <Card
                size="small"
                bodyStyle={{ padding: 0, height: '100%', display: 'flex', flexDirection: 'column' }}
                style={{
                    width: '100%',
                    height: '100%',
                    border: generating ? '1px solid #722ed1' : '1px solid #30363d',
                    boxShadow: generating
                        ? '0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4)'
                        : '0 4px 12px rgba(0,0,0,0.5)',
                    display: 'flex', flexDirection: 'column',
                    borderRadius: 8,
                    background: '#161b22',
                    transition: 'all 0.3s ease',
                    animation: generating ? 'pulse-glow 2s infinite' : 'none'
                }}
            >
                <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>
                    {generating && <div className="shimmer-overlay" />}
                    {data.src ? (
                        <div
                            style={{ position: 'relative', width: '100%', height: '100%' }}
                            onMouseEnter={(e) => e.currentTarget.querySelector('.preview-overlay').style.opacity = 1}
                            onMouseLeave={(e) => e.currentTarget.querySelector('.preview-overlay').style.opacity = 0}
                        >
                            <Image
                                src={data.src}
                                alt="media"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', borderTopLeftRadius: 8, borderTopRightRadius: 8 }}
                                preview={{ visible: previewVisible, onVisibleChange: (vis) => setPreviewVisible(vis) }}
                                height={'100%'}
                                loading="lazy"
                                decoding="async"
                            />
                            {/* Allow clearing/deleting the image to re-enable drop zone easily */}
                            <div
                                className="nodrag delete-btn"
                                onClick={clearImage}
                                style={{
                                    position: 'absolute',
                                    top: 5,
                                    right: 5,
                                    background: 'rgba(0,0,0,0.6)',
                                    borderRadius: '50%',
                                    width: 24,
                                    height: 24,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    cursor: 'pointer',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    zIndex: 10
                                }}
                            >
                                <DeleteOutlined style={{ color: '#fff', fontSize: 12 }} />
                            </div>
                            <style>{`
                                .preview-overlay:hover ~ .delete-btn,
                                .delete-btn:hover {
                                    opacity: 1 !important;
                                }
                            `}</style>
                            <div
                                className="preview-overlay nodrag"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setPreviewVisible(true);
                                }}
                                style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    width: '100%',
                                    height: '100%',
                                    background: 'rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    opacity: 0,
                                    transition: 'opacity 0.2s',
                                    cursor: 'pointer',
                                    borderTopLeftRadius: 8,
                                    borderTopRightRadius: 8
                                }}
                            >
                                <ExpandAltOutlined style={{ fontSize: 24, color: '#fff', dropShadow: '0 2px 4px rgba(0,0,0,0.5)' }} />
                            </div>
                        </div>
                    ) : (
                        // If Upload Node and no image, show Dragger
                        isUploadNode ? (
                            <div className="nodrag" style={{ width: '100%', height: '100%' }}>
                                <Dragger
                                    customRequest={handleUpload}
                                    showUploadList={false}
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        background: 'transparent',
                                        border: 'none',
                                        padding: 0
                                    }}
                                >
                                    <div style={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#8b949e'
                                    }}>
                                        <p className="ant-upload-drag-icon">
                                            <InboxOutlined style={{ color: '#58a6ff', fontSize: 32 }} />
                                        </p>
                                        <p className="ant-upload-text" style={{ color: '#c9d1d9', fontSize: 12 }}>Click or Drag</p>
                                    </div>
                                </Dragger>
                            </div>
                        ) : (
                            // Default placeholder for generator nodes
                            <div style={{
                                height: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                background: '#21262d',
                                color: '#8b949e'
                            }}>
                                <FileImageOutlined style={{ fontSize: 32 }} />
                            </div>
                        )
                    )}

                    {/* Simulated Progress Bar if uploading */}
                    {data.uploading && (
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 8px' }}>
                            <Progress percent={data.progress || 0} showInfo={false} size="small" strokeColor="#52c41a" />
                        </div>
                    )}
                </div>
                <div style={{
                    padding: '8px 12px',
                    background: '#161b22',
                    borderTop: '1px solid #30363d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                    height: 64,
                    overflowX: 'auto',
                    scrollbarWidth: 'none',
                    borderBottomLeftRadius: 8,
                    borderBottomRightRadius: 8
                }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#c9d1d9', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        {data.label || 'Media Asset'}
                    </div>

                    {!isUploadNode && (
                        <div className="nodrag" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                            <InputNumber
                                min={1} max={4}
                                value={numImages}
                                onChange={(v) => updateData('numImages', v)}
                                controls={false}
                                style={{ width: 70, height: 42, borderRadius: 8, textAlign: 'center', background: '#30363d', border: 'none', color: '#fff', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                title="Num Images"
                            />
                            <Select
                                value={aspectRatio}
                                onChange={(v) => updateData('aspectRatio', v)}
                                style={{ width: 120 }}
                                dropdownMatchSelectWidth={false}
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                                variant="borderless"
                                className="pill-select nodrag"
                                onMouseDown={(e) => e.stopPropagation()}
                                options={[
                                    { value: '21:9', label: '21:9' },
                                    { value: '16:9', label: '16:9' },
                                    { value: '3:2', label: '3:2' },
                                    { value: '4:3', label: '4:3' },
                                    { value: '5:4', label: '5:4' },
                                    { value: '1:1', label: '1:1' },
                                    { value: '4:5', label: '4:5' },
                                    { value: '3:4', label: '3:4' },
                                    { value: '2:3', label: '2:3' },
                                    { value: '9:16', label: '9:16' }
                                ]}
                            />
                            <Select
                                value={outputFormat}
                                onChange={(v) => updateData('outputFormat', v)}
                                style={{ width: 110 }}
                                variant="borderless"
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                                className="pill-select nodrag"
                                onMouseDown={(e) => e.stopPropagation()}
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: 'jpeg', label: 'JPEG' },
                                    { value: 'png', label: 'PNG' },
                                    { value: 'webp', label: 'WEBP' }
                                ]}
                            />
                            <Select
                                value={resolution}
                                onChange={(v) => updateData('resolution', v)}
                                style={{ width: 110 }}
                                variant="borderless"
                                getPopupContainer={triggerNode => triggerNode.parentNode}
                                className="pill-select nodrag"
                                onMouseDown={(e) => e.stopPropagation()}
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: '1K', label: '1K' },
                                    { value: '2K', label: '2K' },
                                    { value: '4K', label: '4K' }
                                ]}
                            />
                            {/* Inject custom styles for Pill Selects */}
                            <style>{`
                                .pill-select .ant-select-selector {
                                    background: #30363d !important;
                                    border-radius: 8px !important;
                                    color: #fff !important;
                                    height: 42px !important;
                                    display: flex !important;
                                    align-items: center !important;
                                    font-size: 14px !important;
                                }
                                .pill-select .ant-select-arrow {
                                    color: #8b949e;
                                }
                                @keyframes pulse-glow {
                                    0% {
                                        box-shadow: 0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4);
                                        border-color: #722ed1;
                                    }
                                    50% {
                                        box-shadow: 0 0 25px #9254de, 0 0 50px rgba(82, 196, 26, 0.6);
                                        border-color: #9254de;
                                    }
                                    100% {
                                        box-shadow: 0 0 15px #722ed1, 0 0 30px rgba(82, 196, 26, 0.4);
                                        border-color: #722ed1;
                                    }
                                }
                            `}</style>
                        </div>
                    )}
                </div>
            </Card>
            {/* Thumbnail Gallery */}
            {data.generatedImages && data.generatedImages.length > 1 && (
                <div className="nodrag" style={{
                    position: 'absolute',
                    top: 280,
                    right: -136,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 8,
                    padding: 4,
                }}>
                    {data.generatedImages.map((img, idx) => (
                        <div
                            key={idx}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (data.src === img.url) {
                                    setPreviewVisible(true);
                                } else {
                                    updateData('src', img.url);
                                    setPreviewTarget(img.url);
                                }
                            }}
                            style={{
                                width: 120,
                                height: 120,
                                borderRadius: 6,
                                border: data.src === img.url ? '2px solid #1890ff' : '2px solid transparent',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                background: '#161b22',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.5)',
                                transition: 'all 0.2s'
                            }}
                        >
                            <img src={img.url} alt={`gen-${idx}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default memo(MediaNode);
